import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Dialog, Box, PaperProps, ModalProps } from '@mui/material';
import { IAppState } from 'spw-redux/reducer';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  DepartmentName,
  ScenarioName,
  SecondaryIdentifierLabel,
  WorksheetDialogTitle,
  WorksheetNotesDialogContent,
  WorksheetNotesDot,
  WorksheetNoteTextField,
} from 'spw-components/dialogs/styles';
import {
  createWorksheetNote,
  createTotalProgramNote,
  setWorksheetNotesDialogStatus,
} from 'spw-redux/modules/calculators/actions';
import { ICategoryNote } from 'spw-redux/modules/calculators/types';
import { ColorDot } from 'spw-components/worksheet/styles';
import { MarginLeft8 } from 'spw-shared/globalComponents/containers';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_SPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  NOTE_GENERAL_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import { snakeCase, camelCase, debounce } from 'lodash';
import {
  IDepartment,
  IProject,
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { getColor } from 'spw-shared/utils';
import Draggable from 'react-draggable';
import { colors } from 'spw-theme';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { DraggablePaper } from 'spw-shared/globalComponents/draggable-paper';
import {
  selectCurrentScenario,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { SPWIconButton } from 'spw-shared/globalComponents';
import { Clear } from '@mui/icons-material';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { PlainProgramScenario } from '../../../models/Scenario';
import { PlainWorksheet } from '../../../models/Worksheet';

interface WorksheetNotesProps {
  showDialog: boolean;
  scenario: PlainProgramScenario;
  project: IProject;
  worksheet: PlainWorksheet;
  departments: IDepartment[];
  projectColors: IProjectColors;
}

const GeneralDot = () => {
  return (
    <WorksheetNotesDot className="top">
      <ColorDot color={colors.offWhite} className="colorDot" />
      <MarginLeft8>
        <Box>
          <div>General Notes</div>
        </Box>
      </MarginLeft8>
    </WorksheetNotesDot>
  );
};

function usePrevious(notes: Record<string, string>) {
  const ref = useRef<string>();

  useEffect(() => {
    ref.current = JSON.stringify(notes);
  });

  return ref.current;
}

function PaperComponent(props: PaperProps) {
  const paperRef = useRef();
  return (
    <Draggable
      bounds="#react-container"
      handle="#draggable-worksheet-notes"
      defaultPosition={{ x: 32, y: 32 }}
      nodeRef={paperRef}
    >
      <DraggablePaper {...props} ref={paperRef} />
    </Draggable>
  );
}

const CategoryNotes = ({
  project,
  detailedSharedConfig,
  notes,
  projectColors,
  updateNote,
  saveNote,
}: {
  project: IProject;
  detailedSharedConfig: boolean;
  notes: { [key: string]: string };
  projectColors: IProjectColors;
  updateNote: (
    event: React.ChangeEvent<HTMLInputElement>,
    category: string
  ) => void;
  saveNote: (
    event: React.ChangeEvent<HTMLInputElement>,
    category: string
  ) => void;
}) => {
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const projectSpaceTypeCategories = detailedSharedConfig
    ? project.spaceTypeCategories
    : project.spaceTypeCategories.filter((projectSpaceTypeCategory) =>
        isIWCategoryOrSubcategory(projectSpaceTypeCategory)
      );
  return (
    <Box>
      {projectSpaceTypeCategories?.map(
        (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
          return (
            <Box key={`notes-${projectSpaceTypeCategory.id}`}>
              <Box>
                <WorksheetNotesDot>
                  <ColorDot
                    color={getColor(
                      projectSpaceTypeCategory.defaultSpaceTypeCategory
                        .category,
                      projectColors
                    )}
                    className="colorDot"
                  />
                  <MarginLeft8>
                    <Box>
                      <div>{projectSpaceTypeCategory.name}</div>
                    </Box>
                  </MarginLeft8>
                </WorksheetNotesDot>
              </Box>
              <WorksheetNoteTextField
                label=""
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                multiline
                maxRows={10}
                placeholder="Add notes"
                value={
                  notes[
                    projectSpaceTypeCategory.defaultSpaceTypeCategory.category
                  ]
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateNote(
                    event,
                    projectSpaceTypeCategory.defaultSpaceTypeCategory.category
                  )
                }
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  saveNote(
                    event,
                    projectSpaceTypeCategory.defaultSpaceTypeCategory.category
                  )
                }
                disabled={currentUserIsViewOnly}
              />
            </Box>
          );
        }
      )}
      {!detailedSharedConfig && (
        <Box>
          <WorksheetNotesDot>
            <ColorDot
              color={getColor(SHARED_SPACES_CATEGORY, projectColors)}
              className="colorDot"
            />
            <MarginLeft8>
              <div>Shared Spaces</div>
            </MarginLeft8>
          </WorksheetNotesDot>
          <WorksheetNoteTextField
            label=""
            InputLabelProps={{
              shrink: true,
            }}
            disabled={currentUserIsViewOnly}
            variant="outlined"
            multiline
            maxRows={10}
            placeholder="Add notes"
            value={notes.sharedSpaces}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateNote(event, SHARED_SPACES_CATEGORY)
            }
            onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
              saveNote(event, SHARED_SPACES_CATEGORY)
            }
          />
        </Box>
      )}
    </Box>
  );
};

const WorksheetNotes: React.FC<WorksheetNotesProps> = ({
  showDialog,
  scenario,
  project,
  worksheet,
  projectColors,
}) => {
  if (!worksheet || !scenario) {
    return <></>;
  }

  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const isTotalProgramScenario = isTotalProgram(scenario);
  const dispatch = useDispatch();
  const componentMounted = useRef(false);

  const categories = useMemo(() => {
    const defaultCategories = [
      NOTE_GENERAL_CATEGORY,
      INDIVIDUAL_SPACES_CATEGORY,
    ];

    if (!scenario.isShared) {
      return defaultCategories.concat([
        MEETING_SPACES_CATEGORY,
        AMENITY_SPACES_CATEGORY,
        SUPPORT_SPACES_CATEGORY,
      ]);
    }

    return defaultCategories.concat([SHARED_SPACES_CATEGORY]);
  }, [scenario]);

  const [notes, setNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    const spaceNotes = categories.reduce((categoryNotes, category) => {
      const notesSource = worksheet?.notes || scenario?.notes;
      const categoryNote = notesSource?.find(
        (note) => camelCase(note.category) === category
      );
      if (categoryNote && categoryNote.note) {
        return {
          ...categoryNotes,
          [category]: categoryNote.note,
        } as Record<string, string>;
      }
      return categoryNotes;
    }, {} as Record<string, string>);
    setNotes(spaceNotes);
  }, [worksheet?.notes, scenario.notes, categories]);

  const previousNotes = usePrevious(notes);

  const debouncedSaveWorksheetNote = useRef(
    debounce((note: string, category: string, worksheetId: number) => {
      dispatch(
        createWorksheetNote(
          {
            note: note || '',
            category: snakeCase(category).toUpperCase(),
          },
          worksheetId
        )
      );
    }, 700)
  ).current;

  const debouncedSaveTotalProgramNote = useRef(
    debounce((note: string, category: string, totalProgramId: number) => {
      dispatch(
        createTotalProgramNote(
          {
            note: note || '',
            category: snakeCase(category).toUpperCase(),
          },
          totalProgramId
        )
      );
    }, 700)
  ).current;

  const compareNotes = useCallback(
    (currentNotes: { [key: string]: string }, id: number) => {
      const oldNotes = JSON.parse(previousNotes);
      categories.forEach((category: string) => {
        if (currentNotes[category] !== oldNotes[category]) {
          if (isTotalProgramScenario) {
            debouncedSaveTotalProgramNote(currentNotes[category], category, id);
          } else {
            debouncedSaveWorksheetNote(currentNotes[category], category, id);
          }
        }
      });
    },
    [
      previousNotes,
      categories,
      isTotalProgramScenario,
      debouncedSaveTotalProgramNote,
      debouncedSaveWorksheetNote,
    ]
  );

  useEffect(() => {
    if (previousNotes && isTotalProgramScenario) {
      compareNotes(notes, scenario.id);
    } else if (previousNotes && worksheet?.id) {
      compareNotes(notes, worksheet.id);
    }
  }, [
    previousNotes,
    isTotalProgramScenario,
    notes,
    scenario.id,
    worksheet.id,
    debouncedSaveWorksheetNote,
    debouncedSaveTotalProgramNote,
    compareNotes,
  ]);

  const handleClose = useCallback(() => {
    dispatch(setWorksheetNotesDialogStatus(false));
  }, [dispatch]);

  const onClose = useCallback<ModalProps['onClose']>(
    (_, reason) => {
      if (reason !== 'backdropClick') {
        handleClose();
      }
    },
    [handleClose]
  );

  const saveNote = (
    event: React.FocusEvent<HTMLInputElement>,
    category: string
  ) => {
    const noteText = event.target.value;

    const note: ICategoryNote = {
      note: noteText,
      category: snakeCase(category).toUpperCase(),
    };

    if (isTotalProgramScenario) {
      dispatch(createTotalProgramNote(note, scenario.id));
    } else if (worksheet.id) {
      dispatch(createWorksheetNote(note, worksheet.id));
    }
  };

  const updateNote = (
    event: React.ChangeEvent<HTMLInputElement>,
    category: string
  ) => {
    setNotes({
      ...notes,
      [category]: event.target.value,
    });
  };

  useEffect(() => {
    return () => {
      if (componentMounted.current)
        dispatch(setWorksheetNotesDialogStatus(false));
      componentMounted.current = true;
    };
  }, [dispatch]);

  return (
    <Dialog
      open={showDialog}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-worksheet-notes"
      style={{
        // Trick to prevent the modal backdrop from covering up backgroun items
        // While simultaneously keeping the position: fixed to avoid scroll position jumps
        width: 0,
        height: 0,
      }}
      onClose={onClose}
      disableEnforceFocus
      hideBackdrop
      disableScrollLock
      PaperProps={{
        style: {
          zIndex: 1001,
          left: '30px',
          top: '48px',
          maxHeight: '90%',
        },
      }}
    >
      <WorksheetDialogTitle id="draggable-worksheet-notes">
        <Box display="flex" flexDirection="row">
          <Box flexGrow={1}>Worksheet Notes</Box>
          <SPWIconButton onClick={handleClose} data-testid="close-notes-button">
            <Clear color="primary" fontSize="small" />
          </SPWIconButton>
        </Box>
      </WorksheetDialogTitle>

      <WorksheetNotesDialogContent>
        <Box>
          {scenario?.department && (
            <>
              <DepartmentName>{scenario.department.name}</DepartmentName>
              <SecondaryIdentifierLabel>
                {scenario.department.secondaryIdentifier}
              </SecondaryIdentifierLabel>
            </>
          )}
          <ScenarioName>{scenario.name}</ScenarioName>
          <GeneralDot />
          <WorksheetNoteTextField
            label=""
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            multiline
            maxRows={10}
            placeholder="Add notes"
            value={notes.general}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateNote(event, NOTE_GENERAL_CATEGORY)
            }
            onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
              saveNote(event, NOTE_GENERAL_CATEGORY)
            }
            disabled={currentUserIsViewOnly}
          />
        </Box>

        <CategoryNotes
          project={project}
          detailedSharedConfig={!scenario.isShared}
          notes={notes}
          projectColors={projectColors}
          updateNote={updateNote}
          saveNote={saveNote}
        />
      </WorksheetNotesDialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state: IAppState) => ({
  showDialog: state.calculatorReducer.showWorksheetNotesDialog,
  project: state.projectsReducer.currentProject,
  scenario: selectCurrentScenario(state),
  worksheet: selectWorksheet(state),
  departments: state.projectsReducer.departments,
  projectColors: state.projectsReducer.colors,
});

export default connect(mapStateToProps)(WorksheetNotes);
